import { PROFILE_IMAGE_MAX_BYTES } from "./profile.js";
import { oc } from "@orpc/contract";
import * as z from "zod";
const id = z.string().min(1).max(200);
const name = z
  .string()
  .trim()
  .min(1)
  .max(60)
  .refine((v) => !/[\p{Cc}\p{Cf}]/u.test(v), "Use a readable name");
const role = z.enum(["member", "admin"]);
const context = z.object({ botId: id, departmentId: id, enabled: z.boolean() });
const member = z.object({ userId: id, name: z.string(), email: z.string(), role: z.string() });
const grant = z.object({
  id,
  kind: z.enum(["mcp", "connection"]),
  resourceId: id,
  name: z.string(),
  departmentId: id.nullable(),
  botId: id.nullable(),
  allowAllTools: z.boolean(),
  allowedTools: z.array(z.string()),
});
const resource = z.object({
  id,
  kind: z.enum(["mcp", "connection"]),
  name: z.string(),
  allowAllTools: z.boolean(),
  allowedTools: z.array(z.string()),
});
export const COMPANY_ICON_NAMES = [
  "building",
  "briefcase",
  "globe",
  "landmark",
  "heart",
  "code",
  "megaphone",
  "shopping",
] as const;
export const CompanyIconName = z.enum(COMPANY_ICON_NAMES);
export const CompanyAvatarValue = z
  .string()
  .max(65600)
  .refine(
    (value) =>
      value.startsWith("icon:")
        ? CompanyIconName.safeParse(value.slice(5)).success
        : /^data:image\/webp;base64,[A-Za-z0-9+/]+={0,2}$/.test(value),
    "Invalid Company avatar",
  )
  .nullable();
export const CompanyAvatarChange = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("icon"), icon: CompanyIconName }).strict(),
  z
    .object({
      kind: z.literal("image"),
      adjustment: z
        .object({
          scale: z.number().min(0.5).max(2),
          x: z.number().min(-0.5).max(0.5),
          y: z.number().min(-0.5).max(0.5),
        })
        .strict()
        .optional(),
      base64: z
        .string()
        .min(1)
        .max(Math.ceil(PROFILE_IMAGE_MAX_BYTES / 3) * 4),
    })
    .strict(),
  z.object({ kind: z.literal("none") }).strict(),
]);
export type CompanyAvatarChange = z.infer<typeof CompanyAvatarChange>;
export const SetCompanyAvatar = z.object({ companyId: id, avatar: CompanyAvatarChange }).strict();
export type SetCompanyAvatar = z.infer<typeof SetCompanyAvatar>;
const company = z.object({
  avatar: CompanyAvatarValue.optional(),
  id,
  name: z.string(),
  canManage: z.boolean(),
  departments: z.array(
    z.object({ id, name: z.string(), canManage: z.boolean(), members: z.array(member) }),
  ),
  members: z.array(member),
  bots: z.array(z.object({ id, name: z.string(), contexts: z.array(context) })),
  resources: z.array(resource),
  grants: z.array(grant),
});
export const companiesContract = {
  setAvatar: oc.input(SetCompanyAvatar).output(z.object({ ok: z.literal(true) })),
  list: oc.output(z.array(company)),
  createDepartment: oc.input(z.object({ companyId: id, name })).output(z.object({ id })),
  rename: oc
    .input(z.object({ companyId: id, name, departmentId: id.optional() }))
    .output(z.object({ ok: z.literal(true) })),
  memberships: oc
    .input(z.object({ companyId: id, botId: id, departmentIds: z.array(id).max(32) }))
    .output(z.object({ contexts: z.array(context) })),
  setMember: oc
    .input(
      z.object({
        companyId: id,
        departmentId: id.optional(),
        email: z.string().email(),
        role: role.nullable(),
      }),
    )
    .output(z.object({ ok: z.literal(true) })),
  setGrant: oc
    .input(
      z.object({
        companyId: id,
        id: id.optional(),
        kind: z.enum(["mcp", "connection"]),
        resourceId: id,
        departmentId: id.optional(),
        botId: id.optional(),
        allowAllTools: z.boolean().default(false),
        allowedTools: z.array(z.string().min(1).max(200)).max(300).default([]),
        remove: z.boolean().default(false),
      }),
    )
    .output(z.object({ ok: z.literal(true) })),
};
export type CompanyTree = z.infer<typeof company>[];
